/* eslint-disable no-restricted-syntax */
import type { LitterListPageBreederCategory } from '@presentation/pages';
import { groupBy } from 'lodash/fp';
import { useMemo } from 'react';
import type { LocationDescriptor } from 'history';
import { useRouter } from '@application/utils';
import { useGetBreeders } from './api/useGetBreeders';
import { useSearch } from '../useSearch';
import { useFormatBreederCard } from '../../BreederListPageController/api/useFormatBreederCard';

/**
 * Helper to get the breeders of the selected breeds, and categorise them by breed.
 * Subbreeds like:
 * Chihuahua -> Chihuahua poil court / Chihuahua poil long
 * should be grouped by the base breed name translation, so
 * all of the above will be under the category "Chihuahua"
 *  */

export interface UseBreedersCategoriesParams {
  show: boolean;
}

export const useBreedersCategories = (params: UseBreedersCategoriesParams) => {
  const { show } = params;
  const { valuesFromSearch } = useSearch();
  const { routeLocationDescriptor } = useRouter();
  const { formatBreeder } = useFormatBreederCard();
  const { breeders, breedersLoading, count, breedersRefetch } = useGetBreeders({
    forceSkip: !show,
  });

  const filteredBreeders = useMemo(() => {
    if (!breeders || !breeders.length) return [];
    return breeders
      .map((breeder) => {
        const breederBreeds = breeder.breederBreeds.filter(
          (breed) =>
            valuesFromSearch.breedNames?.includes(String(breed.dogBreed.baseBreedName)) ||
            valuesFromSearch.breedNames?.includes(breed.dogBreed.name),
        );
        return { ...breeder, breederBreeds };
      })
      .filter((item) => item.breederBreeds.length);
  }, [breeders, valuesFromSearch.breedNames]);

  const breederCategories = useMemo((): LitterListPageBreederCategory[] => {
    if (!show || !filteredBreeders?.length) return [];
    const breedTranslationNameByBreedName: any = {};
    for (const breeder of filteredBreeders) {
      // filter breeder breeds
      const filteredBreeds = breeder.breederBreeds.filter(
        (breed) =>
          valuesFromSearch.breedNames?.includes(String(breed.dogBreed.baseBreedName)) ||
          valuesFromSearch.breedNames?.includes(breed.dogBreed.name),
      );
      for (const breed of filteredBreeds) {
        const key = breed.dogBreed.name;
        const value = breed.dogBreed.translations[0].name;
        breedTranslationNameByBreedName[key] = value;
      }
    }
    const formattedBreedersWithBaseBreed = filteredBreeders.map((breeder) => {
      const baseBreedName = breeder.breederBreeds.find((x) => !!x.dogBreed.baseBreedName)?.dogBreed
        .baseBreedName;
      const breedName = breeder.breederBreeds.find((x) => !!x.dogBreed.name)?.dogBreed.name;
      const breedCategoryName =
        breedTranslationNameByBreedName[baseBreedName as any] ??
        breedTranslationNameByBreedName[breedName as any];
      return {
        ...formatBreeder(breeder),
        breedCategoryName,
        breedName,
        baseBreedName,
      };
    });

    const sortedBreedersWithBaseBreed = formattedBreedersWithBaseBreed.sort(
      (a, b) => Number(b.hasLitters) - Number(a.hasLitters),
    );

    const map = groupBy((breeder) => {
      return breeder.breedCategoryName;
    }, sortedBreedersWithBaseBreed);

    return Object.keys(map ?? {}).map((key) => {
      const link: LocationDescriptor = routeLocationDescriptor('breederList', {
        params: [String(map[key]?.[0]?.baseBreedName ?? map[key]?.[0]?.breedName)],
      });
      const canLoadMoreRelatedBreeders = map[key].length > 4 && count > 4;
      return {
        label: key,
        breeders: map[key].splice(0, 4),
        canLoadMoreRelatedBreeders,
        link,
      };
    });
  }, [
    show,
    filteredBreeders,
    valuesFromSearch.breedNames,
    formatBreeder,
    routeLocationDescriptor,
    count,
  ]);

  return { breederCategories, breedersLoading, breedersRefetch };
};
